<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDuration, formatPhone } from 'dashboard/helper/relayVoicePhone';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import PhoneKeypad from './PhoneKeypad.vue';

// The call in progress: who, how long, and the controls an agent needs.
const props = defineProps({
  // 'incoming' | 'outgoing' | 'ongoing'
  state: { type: String, required: true },
  party: { type: Object, required: true },
  seconds: { type: Number, default: 0 },
  muted: { type: Boolean, default: false },
  held: { type: Boolean, default: false },
  digits: { type: String, default: '' },
  canTransfer: { type: Boolean, default: false },
  transferNote: { type: String, default: '' },
  busy: { type: Boolean, default: false },
});
const emit = defineEmits([
  'accept',
  'decline',
  'end',
  'toggleMute',
  'toggleHold',
  'digit',
  'transfer',
  'openConversation',
]);
const { t } = useI18n();
const showKeypad = ref(false);

const isOngoing = computed(() => props.state === 'ongoing');
const displayName = computed(
  () =>
    props.party.name ||
    (props.party.extension
      ? t('RELAY_VOICE.CALLS.INTERNAL', { extension: props.party.extension })
      : formatPhone(props.party.number) || t('RELAY_VOICE.UNKNOWN_CALLER'))
);
const subtitle = computed(() => {
  if (props.state === 'incoming') return t('RELAY_VOICE.INCOMING');
  if (props.state === 'outgoing') return t('RELAY_VOICE.OUTGOING');
  if (props.held) return t('RELAY_VOICE.ON_HOLD');
  return formatDuration(props.seconds);
});
const controls = computed(() => [
  {
    id: 'mute',
    icon: props.muted ? 'i-ph-microphone-slash-bold' : 'i-ph-microphone-bold',
    label: props.muted ? t('RELAY_VOICE.UNMUTE') : t('RELAY_VOICE.MUTE'),
    active: props.muted,
    action: () => emit('toggleMute'),
  },
  {
    id: 'keypad',
    icon: 'i-ph-dots-nine-bold',
    label: showKeypad.value
      ? t('RELAY_VOICE.HIDE_KEYPAD')
      : t('RELAY_VOICE.KEYPAD'),
    active: showKeypad.value,
    action: () => {
      showKeypad.value = !showKeypad.value;
    },
  },
  {
    id: 'hold',
    icon: props.held ? 'i-ph-play-bold' : 'i-ph-pause-bold',
    label: props.held ? t('RELAY_VOICE.RESUME') : t('RELAY_VOICE.HOLD'),
    active: props.held,
    action: () => emit('toggleHold'),
  },
  {
    id: 'transfer',
    icon: 'i-ph-arrow-bend-up-right-bold',
    label: t('RELAY_VOICE.TRANSFER'),
    active: false,
    disabled: !props.canTransfer,
    action: () => emit('transfer'),
  },
]);
</script>

<template>
  <div class="flex flex-col items-center gap-4 pt-2" data-tour="phone-in-call">
    <div class="flex flex-col items-center gap-2 text-center min-w-0 w-full">
      <span
        class="rounded-full"
        :class="
          state === 'incoming' ? 'ring-4 ring-n-teal-6 animate-pulse' : ''
        "
      >
        <Avatar :src="party.avatarUrl" :name="displayName" :size="56" />
      </span>
      <p class="text-base font-semibold text-n-slate-12 truncate max-w-full">
        {{ displayName }}
      </p>
      <p
        v-if="party.name && party.number"
        class="text-xs text-n-slate-10 tabular-nums -mt-1.5"
      >
        {{ formatPhone(party.number) }}
      </p>
      <p
        class="text-sm tabular-nums"
        :class="held ? 'text-n-amber-11' : 'text-n-slate-11'"
        aria-live="polite"
      >
        {{ subtitle }}
      </p>
      <button
        v-if="party.conversationId"
        type="button"
        class="text-xs font-medium text-n-blue-text hover:underline flex items-center gap-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand rounded"
        @click="emit('openConversation')"
      >
        <span class="i-ph-chat-circle-text-bold size-3.5" aria-hidden="true" />
        {{ t('RELAY_VOICE.OPEN_CONVERSATION') }}
      </button>
    </div>

    <template v-if="state === 'incoming'">
      <div class="flex gap-10 pb-2">
        <div class="flex flex-col items-center gap-1.5">
          <button
            type="button"
            class="size-14 rounded-full bg-n-ruby-9 text-white flex items-center justify-center hover:bg-n-ruby-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-ruby-9"
            :aria-label="t('RELAY_VOICE.DECLINE')"
            :disabled="busy"
            @click="emit('decline')"
          >
            <span
              class="i-ph-phone-disconnect-bold size-6"
              aria-hidden="true"
            />
          </button>
          <span class="text-xs text-n-slate-11">{{
            t('RELAY_VOICE.DECLINE')
          }}</span>
        </div>
        <div class="flex flex-col items-center gap-1.5">
          <button
            type="button"
            class="size-14 rounded-full bg-n-teal-9 text-white flex items-center justify-center hover:bg-n-teal-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-teal-9"
            :aria-label="t('RELAY_VOICE.ACCEPT')"
            :disabled="busy"
            @click="emit('accept')"
          >
            <span class="i-ph-phone-bold size-6" aria-hidden="true" />
          </button>
          <span class="text-xs text-n-slate-11">{{
            t('RELAY_VOICE.ACCEPT')
          }}</span>
        </div>
      </div>
    </template>

    <template v-else>
      <div
        v-if="showKeypad && isOngoing"
        class="flex flex-col items-center gap-2 w-full"
      >
        <p
          class="text-sm font-medium tabular-nums text-n-slate-12 h-5 tracking-widest"
          aria-live="polite"
        >
          {{ digits }}
        </p>
        <PhoneKeypad
          compact
          :disabled="held"
          @press="key => emit('digit', key)"
        />
      </div>
      <div v-else class="grid grid-cols-4 gap-3 w-full">
        <div
          v-for="control in controls"
          :key="control.id"
          class="flex flex-col items-center gap-1.5"
        >
          <button
            type="button"
            :aria-pressed="
              control.id === 'transfer' ? undefined : control.active
            "
            :aria-label="control.label"
            :disabled="!isOngoing || control.disabled || busy"
            class="size-12 rounded-full flex items-center justify-center transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand disabled:opacity-40"
            :class="
              control.active
                ? 'bg-n-slate-12 text-n-solid-1'
                : 'bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-3'
            "
            @click="control.action"
          >
            <span :class="control.icon" class="size-5" aria-hidden="true" />
          </button>
          <span
            class="text-[0.6875rem] text-n-slate-11 text-center leading-tight"
          >
            {{ control.label }}
          </span>
        </div>
      </div>
      <p v-if="transferNote" class="text-xs text-n-slate-10 text-center -mt-1">
        {{ transferNote }}
      </p>
      <div class="flex items-center gap-3 pb-1">
        <button
          v-if="showKeypad && isOngoing"
          type="button"
          class="size-10 rounded-full bg-n-alpha-2 text-n-slate-12 flex items-center justify-center hover:bg-n-alpha-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
          :aria-label="t('RELAY_VOICE.HIDE_KEYPAD')"
          @click="showKeypad = false"
        >
          <span class="i-ph-x-bold size-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          class="h-12 px-6 rounded-full bg-n-ruby-9 text-white flex items-center justify-center gap-2 text-sm font-medium hover:bg-n-ruby-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-ruby-9"
          :disabled="busy"
          @click="emit('end')"
        >
          <span class="i-ph-phone-disconnect-bold size-5" aria-hidden="true" />
          {{ t('RELAY_VOICE.END') }}
        </button>
      </div>
    </template>
  </div>
</template>
